/** @module form */

import { toElements } from '../utils';

// `password` is excluded even from the `data-char-counter` opt-in: a visible count leaks the length of a
// password being typed to anyone watching the screen
const COUNTABLE_TYPES = ['text', 'search', 'url', 'tel', 'email'],
    // below this, a `maxlength` describes a format (date, time, postcode) rather than a writing budget
    DEFAULT_MIN_LENGTH = 25;

// `data-char-counter` / `data-no-char-counter` come from the CF7 form tag options of the same name (cf7.php)
function isCountable(control, minLength) {
    const max = parseInt(control.getAttribute('maxlength'), 10);

    if ('noCharCounter' in control.dataset || !(max > 0)) {
        return false;
    }

    if (control.tagName !== 'TEXTAREA' && COUNTABLE_TYPES.includes(control.type) === false) {
        return false;
    }

    return 'charCounter' in control.dataset || max >= minLength;
}

/**
 * Shows a character counter inside every text control that has a character limit, so users can see how much
 * they may still enter.
 *
 * @param {object} [options] - Options, see {@link initCharCounters}. Additionally, `minLength` sets the
 *  `maxlength` from which on a counter is added automatically.
 *
 * @returns {Promise<void>} - Resolves immediately if no eligible control is on the current page, or once all
 *  counters are initialized.
 */
export async function charCounter(options = {}) {
    const minLength = typeof options.minLength === 'number' ? options.minLength : DEFAULT_MIN_LENGTH,
        controls = toElements('form input[maxlength], form textarea[maxlength]').filter((control) =>
            isCountable(control, minLength),
        );

    if (controls.length) {
        const { initCharCounters } = await import(/* webpackMode: "lazy" */ './inc/char-counter.async');

        initCharCounters(controls, options);
    }
}
